import React, { Component } from "reactn";
import { List as AppList, ListAction, ListField, ListIcon } from "../../../components/List";
import Api from "../../../../core/api";
import {
    ShowErrorDialog,
    ShowDialog,
    ShowMessageDialog,
    ShowConfirmationDialog,
    ShowSnackBar
} from "../../../components/Dialogs/BaseDialog";
import log from "../../../../core/logger";
import AddTeamSeatDialog from "../dialogs/AddTeamSeatDialog";
import { app } from "js/namespaces";
import { trackActivity } from "../../../../core/utilities/utilities";
import { getUsedSeatCount } from "../UserOptionsContainer";
import { BlueButton } from "../../../components/UiComponents";
import { TEAM_USER_ROLES, TEAM_USER_LICENSE_STATUS, AnalyticsRolesAndLicensesMap } from "common/constants";
import { ds } from "js/core/models/dataService";
import { UserProfile } from "js/react/components/Avatar";
import { Select, MenuItem, Divider } from "@material-ui/core";
import styled from "styled-components";
import { ResendInviteDialog } from "js/react/components/InviteInput/ResendInviteDialog";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";

const PendingInvite = styled.div`
    text-transform: uppercase;
    font-size: 13px;
    color: #555;
`;

function getRoleUpdateText(user, role) {
    let text = { title: "", message: "" };

    switch (role) {
        case (TEAM_USER_ROLES.MEMBER):
        case (TEAM_USER_ROLES.MEMBER_LICENSED):
            text.title = `Do you want to change ${user.displayName || user.email} to a member?`;
            text.message = "They will only have access to shared team content.";
            break;
        case (TEAM_USER_ROLES.LIBRARIAN):
            text.title = `Do you want to change ${user.displayName || user.email} to a librarian?`;
            text.message = "They will be able add and manage slide, presentations, themes, and assets for the team.";
            break;
        case (TEAM_USER_ROLES.OWNER):
            text.title = `Do you want to change ${user.displayName || user.email} to a owner?`;
            text.message = "They will be able to manage payment info, add/remove members, add/remove seats, and change team permissions on resources.";
            break;
    }
    return text;
}

class TeamMembersList extends Component {
    state = {
        menuAnchorEl: null,
    };

    refreshApp = () => {
        setTimeout(() => {
            window.location.href = "/";
        }, 50);
    };

    handleRemoveInvite = async email => {
        const { team, getTeam } = this.props;
        try {
            await Api.teams.put({
                teamId: team.id,
                type: "remove_invites",
                emails: [email]
            });
            trackActivity("Organization", "InviteRemoved", null, null, { email }, { audit: true });
            getTeam();
        } catch (err) {
            if (err) {
                log.error(err);
                ShowErrorDialog({
                    error: "Failed to remove invite",
                    message: "Something went wrong. Please try again and reach out to support@beautiful.ai if the issue persists."
                });
            }
        }
    };

    handleRoleUpdate = async (user, role) => {
        const { org, team, getTeam } = this.props;
        const { title, message } = getRoleUpdateText(user, role);
        ShowConfirmationDialog({
            title,
            message
        }).then(async accept => {
            if (!accept) {
                return;
            }
            try {
                await Api.teams.put({
                    teamId: team.id,
                    orgId: org.id,
                    type: "update_roles",
                    userIds: [user.id],
                    role
                });

                const props = {
                    workspace_id: org.id,
                    affected_user: user.id,
                    old_role: AnalyticsRolesAndLicensesMap[user.role],
                    new_role: AnalyticsRolesAndLicensesMap[role],
                    old_license: AnalyticsRolesAndLicensesMap[user.license],
                    new_license: AnalyticsRolesAndLicensesMap[user.license]
                };
                trackActivity("Organization", "UserStatusChanged", null, null, props, { audit: true });

                getTeam();
            } catch (err) {
                if (err) {
                    log.error(err);
                    ShowErrorDialog({
                        error: `Failed to update user to ${role}`,
                        message: "Something went wrong. Please try again and reach out to support@beautiful.ai if the issue persists."
                    });
                }
            }
        });
    };

    handleUpgradeMember = async user => {
        const { team, getTeam, getFreshState, org, isActiveSubscription, totalSeatCount } = this.props;
        const usedSeatCount = getUsedSeatCount(team.members);

        //If there are not enough seats show AddTeamSeatDialog
        if (totalSeatCount - usedSeatCount <= 0) {
            if (isActiveSubscription) {
                ShowDialog(AddTeamSeatDialog, {
                    userId: user.id,
                    team,
                    org,
                    teamName: team.name,
                    teamMemberCount: team.members.length,
                    usedSeatCount,
                    currentSeatCount: totalSeatCount,
                    callback: () => getFreshState()
                });
            } else {
                ShowErrorDialog({ title: "Unable to add seats", message: "Seems like you cancelled the team subscription." });
            }
            return;
        }

        //If there are enough seats and the team is a pro-team assign a seat to a user
        try {
            await Api.teams.put({
                orgId: org.id,
                teamId: team.id,
                type: "add_user_to_seat",
                userIds: [user.id]
            });

            const props = {
                workspace_id: org.id,
                affected_user: user.id,
                old_role: AnalyticsRolesAndLicensesMap[user.role],
                new_role: AnalyticsRolesAndLicensesMap[user.role],
                old_license: AnalyticsRolesAndLicensesMap["free"],
                new_license: AnalyticsRolesAndLicensesMap["team_pro"]
            };
            trackActivity("Organization", "UserStatusChanged", null, null, props, { audit: true });

            getTeam();
            if (user.id === app.user.id) {
                ShowMessageDialog({
                    title: "Your seat has been added.",
                    message: "Your browser will be refreshed for the change to take effect.",
                    onClick: this.refreshApp,
                    onClose: this.refreshApp
                });
            }
        } catch (err) {
            if (err) {
                log.error(err);
                ShowErrorDialog({
                    error: "Failed to upgrade user",
                    message: "Something went wrong. Please try again and reach out to support@beautiful.ai if the issue persists."
                });
            }
        }
    };

    handleDowngradeMember = async user => {
        const { team, getTeam, org } = this.props;

        const title = user.id === app.user.id
            ? `Are you sure you want to downgrade yourself ?`
            : `Are you sure you want to downgrade ${user.displayName || user.email}`;
        const message = user.id === app.user.id
            ? "You will lose all Team features (fonts, folders, unlimited slides, etc..) if your billing is not managed by another team or yourself."
            : "They will also lose Team features (fonts, folders, unlimited slides, etc..) if this team is their only source of Team features and they will be changed to a Member role.";

        ShowConfirmationDialog({
            title,
            message
        }).then(async accept => {
            if (!accept) {
                return;
            }
            try {
                await Api.teams.put({
                    orgId: org.id,
                    teamId: team.id,
                    type: "remove_user_from_seat",
                    userIds: [user.id]
                });

                const props = {
                    workspace_id: org.id,
                    affected_user: user.id,
                    old_role: AnalyticsRolesAndLicensesMap[user.role],
                    new_role: AnalyticsRolesAndLicensesMap[user.role],
                    old_license: AnalyticsRolesAndLicensesMap["team_pro"],
                    new_license: AnalyticsRolesAndLicensesMap["free"]
                };
                trackActivity("Organization", "UserStatusChanged", null, null, props, { audit: true });

                getTeam();
                if (user.id === app.user.id) {
                    ShowMessageDialog({
                        title: "Your seat has been removed.",
                        message: "Your browser will be refreshed for the change to take effect.",
                        onClick: this.refreshApp,
                        onClose: this.refreshApp
                    });
                }
            } catch (err) {
                if (err) {
                    log.error(err);
                    ShowErrorDialog({
                        error: "Failed to downgrade user",
                        message: "Something went wrong. Please try again and reach out to support@beautiful.ai if the issue persists."
                    });
                }
            }
        });
    };

    getMemberPlan = member => {
        if (member.pending) {
            return member.license;
        }

        if (ds.teams.defaultTeamForOrg(this.props.organizationId).checkIfUserHasSeat(member)) {
            return "team_pro";
        }

        return "free";
    };

    renderLicenseDropdown = member => {
        const {
            canManageTeam,
            team,
            getTeam,
            isDefaultTeam,
            handleRemoveMember,
        } = this.props;

        let canChangeLicense = (
            (
                member.role !== TEAM_USER_ROLES.LIBRARIAN &&
                member.role !== TEAM_USER_ROLES.OWNER
            )
        );

        let showRemove = (
            isDefaultTeam &&
            app.user.getEmail() !== member.email
        );

        const isDisabled = (
            !canManageTeam ||
            app.user.id === member.id ||
            (
                !canChangeLicense &&
                !showRemove
            )
        );

        if (!member.license) {
            return "None";
        }

        const handleChange = event => {
            switch (event.target.value) {
                case TEAM_USER_LICENSE_STATUS.TEAM_PRO:
                    this.handleUpgradeMember(member);
                    break;
                case TEAM_USER_LICENSE_STATUS.FREE:
                    this.handleDowngradeMember(member);
                    break;
                case "remove":
                    if (member.pending) {
                        this.handleRemoveInvite(member.email);
                    } else {
                        handleRemoveMember(
                            team,
                            member,
                            team.members,
                            "manage",
                            getTeam,
                            isDefaultTeam
                        );
                    }
                    break;
            }
        };

        return (
            <Select value={member.license}
                onChange={handleChange}
                disabled={isDisabled}
                disableUnderline
            >
                <MenuItem
                    key={TEAM_USER_LICENSE_STATUS.TEAM_PRO}
                    value={TEAM_USER_LICENSE_STATUS.TEAM_PRO}
                    disabled={!canChangeLicense}
                >Team Pro</MenuItem>
                <MenuItem
                    key={TEAM_USER_LICENSE_STATUS.FREE}
                    value={TEAM_USER_LICENSE_STATUS.FREE}
                    disabled={!canChangeLicense}
                >Free</MenuItem>
                {
                    showRemove &&
                    <Divider />
                }
                {
                    showRemove &&
                    <MenuItem
                        key="remove"
                        value="remove"
                    >Remove Member</MenuItem>
                }
            </Select>
        );
    }

    renderRoleDropdown = member => {
        const { canManageTeam } = this.props;
        const isDisabled = (
            app.user.id === member.id ||
            member.license === "free" ||
            member.pending
        );
        const options = [
            {
                label: "Member",
                value: member.license === "free" ? TEAM_USER_ROLES.MEMBER : TEAM_USER_ROLES.MEMBER_LICENSED,
                isDisabled
            },
            { label: "Librarian", value: TEAM_USER_ROLES.LIBRARIAN, isDisabled },
            { label: "Owner", value: TEAM_USER_ROLES.OWNER, isDisabled }
        ];

        return (
            <Select value={member.role}
                onChange={event => this.handleRoleUpdate(member, event.target.value)}
                disabled={!canManageTeam || isDisabled}
                disableUnderline
            >
                {options.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
        );
    }

    sanitizeMembersForUI = team => {
        return team.members.map(member => {
            return {
                id: member.id,
                url: member.url,
                displayName: member.displayName || member.email,
                email: member.email,
                photoURL: member.photoURL,
                role: member.role,
                license: this.getMemberPlan(member),
                pending: !!member.pending
            };
        });
    }

    handleResendInvite = member => {
        let {
            team,
        } = this.props;
        let email = member.email;
        let license = member.license;

        ShowDialog(ResendInviteDialog, {
            user: member,
            onSendEmail: async () => {
                await Api.teams.put({
                    teamId: team.id,
                    type: "add_users",
                    members: [member],
                    orgId: team.orgId,
                });

                const props = {
                    team_id: team.id,
                    license,
                    recipient_email: email,
                    sender_email: app.user.getEmail(),
                };
                trackActivity("Organization", "ResendTeamInvite", null, null, props, { audit: true });
                ShowSnackBar({ message: `Invitation Resent to ${email}` });
            },
            onCopyInviteLink: async () => {
                let { teamInviteUrl } = await Api.teamInviteUrl.post({
                    teamId: team.id,
                    invitedUser: member,
                });

                await clipboardWrite({
                    [ClipboardType.TEXT]: teamInviteUrl,
                });

                const props = {
                    team_id: team.id,
                    license,
                    recipient_email: email,
                    sender_email: app.user.getEmail(),
                    teamInviteUrl,
                };
                trackActivity("Organization", "CopyTeamInviteLink", null, null, props, { audit: true });
                ShowSnackBar({ message: "Invitation Copied to Clipboard" });
            },
        });
    }

    render() {
        const { team, isDefaultTeam, canManageTeam } = this.props;

        const members = this.sanitizeMembersForUI(team);

        return (
            <AppList data={members} showHeader>
                <ListField
                    field="displayName"
                    name="Member"
                    width="2fr"
                    type="bold"
                    sortable
                    renderer={member => <UserProfile profile={member}/>}
                />
                {
                    isDefaultTeam &&
                    <ListField
                        field="role"
                        name="Role"
                        isSelectField
                        sortable
                        renderer={this.renderRoleDropdown}
                    />
                }
                <ListField
                    field="pending"
                    sortable
                    renderer={member => {
                        if (!member.pending) {
                            return null;
                        }

                        return (
                            <PendingInvite>
                                <span>pending invite</span>
                            </PendingInvite>
                        );
                    }}
                />
                {
                    isDefaultTeam &&
                    <ListField
                        field="license"
                        name="License"
                        sortable
                        isSelectField
                        renderer={this.renderLicenseDropdown}
                    />
                }
                {
                    canManageTeam &&
                    <ListAction
                        width={160}
                        renderer={member => {
                            if (
                                (
                                    isDefaultTeam &&
                                    app.user.getEmail() === member.email
                                ) ||
                                !member.pending
                            ) {
                                return null;
                            }

                            return (
                                <BlueButton
                                    className="resend-invite-button"
                                    onClick={() => this.handleResendInvite(member)}
                                >Resend Invite</BlueButton>
                            );
                        }}
                    />
                }
            </AppList>
        );
    }
}

export default TeamMembersList;
